import type { AdminFeatureState, FeatureTier, Tier } from '@boardflight/shared';

/**
 * Every feature, as one page's half of the question answers it (§2) — read-only.
 *
 * What somebody has follows from two values, the plan of the team they act in and their own tier,
 * and nothing else: there is no grant to set and no switch per feature. So an account's page and a
 * team's page can each say only what *their* half settles. The other half's features read "per
 * team" or "per person" rather than on or off, because either word would be a guess — and what a
 * person actually has inside one team is the pair, which the account's Teams tab lists.
 *
 * Changing what is on here means changing the half this page is about: the account's tier, or the
 * team's plan, both in the header above. Moving a feature to another tier is the Features screen's,
 * and changes it for everybody at once.
 */
export function FeatureList({ features, other }: {
  features: AdminFeatureState[];
  /** What a row the other half decides reads as: "per team" on an account, "per person" on a team. */
  other: string;
}) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Feature</th>
            <th>Tier</th>
            <th>Here</th>
          </tr>
        </thead>
        <tbody>
          {features.map((row) => (
            <tr key={row.key}>
              <td>
                <div className="mono">{row.key}</div>
                {/* What having it does, beside whether this page has it. Almost every key is
                    enforced at a narrower point than its name suggests, and this is where somebody
                    is about to move a tier or a plan for a named person or team. */}
                <p className="muted small">{row.effect}</p>
              </td>
              <td>
                <TierPill tier={row.tier} />
              </td>
              <td>
                {row.enabled === null ? (
                  <span className="muted">{other}</span>
                ) : (
                  <span className={row.enabled ? 'pill on' : 'pill off'}>{row.enabled ? 'on' : 'off'}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A feature's tier or a person's, as a pill. Everybody's — `free` — is plain, nobody's — `off` — is
 * the console's off, and anything between is marked, with its word saying who.
 */
export function TierPill({ tier }: { tier: FeatureTier | Tier }) {
  return <span className={tier === 'free' ? 'pill' : tier === 'off' ? 'pill off' : 'pill paid'}>{tier}</span>;
}
